import { MUSIC_TELEMETRY_SCHEMA, type ControllerMirrorAudio, type MusicTelemetry } from './types';

export const AUDIO_TELEMETRY_SCHEMA = 'audio-telemetry/2';
export interface AudioModelHealth {
  state?: string;
  detail?: string;
}

/** The common display shape, whether delivered by a direct notification or the cloud mirror. */
export interface AudioTelemetryFeed {
  schema?: string;
  state?: string;
  inputId?: string;
  startedAt?: string;
  expiresAt?: string;
  inputSignal?: string;
  signalSinceMs?: number;
  peakDb?: number;
  levelHistory?: Array<[number, number]>;
  levelHistorySamples?: number;
  meter?: number;
  rmsDb?: number;
  clip?: boolean;
  channels?: Array<{ peakDb: number; rmsDb: number; clipped: boolean }>;
  gate?: number;
  gateState?: string;
  beatConfidence?: number;
  bpm?: number | null;
  bpmRaw?: number | null;
  /** Only the leased `music-telemetry/1` node reports it; every other feed leaves it undefined. */
  locked?: boolean;
  octaveHeld?: boolean;
  rangeFolded?: boolean;
  tempoRange?: number[];
  songChangeAt?: number | null;
  analysisClockS?: number;
  trackerConfidence?: number;
  xrunCount?: number;
  renderFps?: number;
  renderTargetFps?: number;
  renderRateShortfall?: boolean;
  analysisHz?: number;
  outputHz?: number;
  latencyMs?: number;
  beatTracker?: string;
  beatPhase?: number;
  captureBlock?: number;
  sampleRate?: number;
  captureOverflows?: number;
  droppedHops?: number;
  captureMaxGapMs?: number;
  captureState?: string;
  captureRestarts?: number;
  patchSequence?: number;
  patchError?: string | null;
  models?: Record<string, AudioModelHealth>;
}

export interface AudioTimingStatus {
  fps?: number;
  outputHz?: number;
  telemetry?: AudioTelemetryFeed;
}

/** Initial status structs encode missing measurements as zero. */
function positive(value: number | undefined): number | undefined {
  return Number.isFinite(value) && (value as number) > 0 ? value : undefined;
}

/** Whether a reported signal verdict means sound is actually arriving. Undefined until it can tell. */
export function receivingAudio(signal: string | undefined): boolean | undefined {
  switch (String(signal ?? '').trim().toLowerCase()) {
    case 'music': case 'signal': return true;
    case 'silence': case 'none': return false;
    default: return undefined;
  }
}

/**
 * The one line at the head of the room's music pane. It answers the only question an operator has
 * before they look at anything else: is this room hearing music? The room having an input and the
 * input carrying sound are different facts, so they get different words.
 */
export function musicInputStatusLine(input: {
  roomHasInput: boolean;
  inputSignal?: string;
}): string {
  if (!input.roomHasInput) return 'No music input';
  return receivingAudio(input.inputSignal) === false ? 'Input not receiving audio' : 'Music input connected';
}

/** Status telemetry reads the current sidecar tag and existing `/1` room data. */
export function audioTelemetryFromStatus(status: AudioTimingStatus | undefined): AudioTelemetryFeed | undefined {
  return status?.telemetry?.schema === AUDIO_TELEMETRY_SCHEMA || status?.telemetry?.schema === 'audio-telemetry/1'
    ? status.telemetry : undefined;
}

/**
 * Copy only the fields named by B12 from the live-state mirror into the screen's common readout.
 * Unlike the direct sidecar notification this projection has no schema tag: its shape is gated by the
 * controller-state contract itself.
 */
export function audioTelemetryFromMirror(audio: ControllerMirrorAudio | null | undefined): AudioTelemetryFeed | undefined {
  if (!audio) return undefined;
  const signalReady = ['music', 'signal', 'silence', 'none']
    .includes(String(audio.inputSignal ?? '').trim().toLowerCase());
  return {
    state: audio.state,
    inputId: audio.inputId,
    startedAt: audio.startedAt,
    expiresAt: audio.expiresAt,
    inputSignal: audio.inputSignal,
    signalSinceMs: audio.signalSinceMs,
    // A newly-started Go monitor has a zero-value telemetry struct until its first 4 Hz sidecar frame.
    // Zero dBFS means clipping, so never present those transport defaults as a real measurement.
    peakDb: signalReady && Number.isFinite(audio.peakDb) ? audio.peakDb : undefined,
    levelHistory: signalReady && Array.isArray(audio.levelHistory) ? audio.levelHistory : undefined,
    levelHistorySamples: signalReady ? audio.levelHistorySamples : undefined,
    renderFps: audio.renderFps,
    renderTargetFps: audio.renderTargetFps,
    renderRateShortfall: audio.renderRateShortfall,
    analysisHz: audio.analysisHz,
    outputHz: audio.outputHz,
    latencyMs: audio.latencyMs,
    captureBlock: audio.captureBlock,
    beatTracker: audio.beatTracker,
    beatPhase: audio.beatPhase,
    bpm: audio.bpm,
    bpmRaw: audio.bpmRaw,
    octaveHeld: audio.octaveHeld,
    rangeFolded: audio.rangeFolded,
    tempoRange: audio.tempoRange,
    songChangeAt: audio.songChangeAt,
    analysisClockS: audio.analysisClockS,
    trackerConfidence: signalReady ? audio.trackerConfidence : undefined,
    beatConfidence: audio.beatConfidence,
    meter: audio.meter,
    clip: audio.clip,
    rmsDb: signalReady && Number.isFinite(audio.rmsDb) ? audio.rmsDb : undefined,
    gateState: audio.gateState,
    xrunCount: audio.xrunCount,
    captureOverflows: audio.captureOverflows,
    droppedHops: audio.droppedHops,
    captureMaxGapMs: audio.captureMaxGapMs,
    captureState: audio.captureState,
    captureRestarts: audio.captureRestarts,
  };
}

/**
 * Copy the leased `music-telemetry/1` node into the screen's common readout.
 *
 * The node is the hosted console's only source of a tempo: controller 1.7.20 retired `audioMonitor` from
 * the mirror and `json:"-"`d the moving effect-clock fields, so from that release until this one a locked
 * room showed a blank Tempo card. The shape is narrow on purpose — it carries exactly what this screen
 * draws — so this is a copy and not a projection with rules of its own.
 *
 * The one rule it does apply is the same one the mirror projection applies, for the same reason: a monitor
 * that has not received its first sidecar frame reports zeroes, and zero dBFS means CLIPPING. Presenting a
 * transport default as a measurement is exactly the lie this screen exists to stop telling, so the meters
 * are withheld until the analyzer has said something about the signal.
 */
export function audioTelemetryFromMusicTelemetry(
  music: MusicTelemetry | null | undefined,
): AudioTelemetryFeed | undefined {
  if (!music || (music.schema !== MUSIC_TELEMETRY_SCHEMA && music.schema !== 'music-telemetry/1')) return undefined;
  const signalReady = ['music', 'signal', 'silence', 'none']
    .includes(String(music.inputSignal ?? '').trim().toLowerCase());
  const channels = Array.isArray(music.channels) && music.channels.length >= 1 && music.channels.length <= 8
    && music.channels.every((channel) => Number.isFinite(channel?.peakDb)
      && Number.isFinite(channel?.rmsDb) && typeof channel?.clipped === 'boolean')
    ? music.channels : undefined;
  return {
    inputId: music.inputId,
    inputSignal: music.inputSignal,
    bpm: music.bpm,
    // Only `true` is a claim. A 1.7.20-shaped node omits the member, and a console that read that
    // silence as `false` would be inventing the analyzer's verdict rather than reporting it.
    locked: music.locked === true ? true : undefined,
    peakDb: signalReady && Number.isFinite(music.peakDb) ? music.peakDb : undefined,
    rmsDb: signalReady && Number.isFinite(music.rmsDb) ? music.rmsDb : undefined,
    channels: signalReady && music.schema === MUSIC_TELEMETRY_SCHEMA ? channels : undefined,
  };
}

function tempoNumber(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/** Plain operator diagnosis for a reported tempo that differs from the elected tracker's octave. */
export function tempoPresentation(telemetry: AudioTelemetryFeed | undefined): string {
  const reported = positive(telemetry?.bpm ?? undefined);
  if (reported == null) return '—';
  const raw = positive(telemetry?.bpmRaw ?? undefined);
  if (telemetry?.octaveHeld) {
    if (raw == null) return `Tempo ${tempoNumber(reported)} — holding octave`;
    if (Math.abs(raw - reported) < 0.05) return `${tempoNumber(reported)} bpm`;
    const ratio = raw / reported;
    const octave = Math.abs(ratio / 2 - 1) <= .04 || Math.abs(ratio / .5 - 1) <= .04;
    return `Tempo ${tempoNumber(reported)} · tracker says ${tempoNumber(raw)} — ${octave ? 'holding octave' : 'stabilizing tempo'}`;
  }
  if (telemetry?.rangeFolded) {
    if (raw == null || Math.abs(raw - reported) < 0.05) {
      return `Tempo ${tempoNumber(reported)} — range prior selected octave`;
    }
    return `Tempo ${tempoNumber(reported)} · tracker says ${tempoNumber(raw)} — range prior selected octave`;
  }
  return `${tempoNumber(reported)} bpm`;
}

/** Human-readable age for the most recent boundary on the sidecar's session-local clock. */
export function songChangePresentation(telemetry: AudioTelemetryFeed | undefined): string | undefined {
  const changed = telemetry?.songChangeAt;
  const clock = telemetry?.analysisClockS;
  if (!Number.isFinite(changed) || !Number.isFinite(clock) || Number(changed) < 0 || Number(clock) < 0) {
    return undefined;
  }
  const seconds = Math.max(0, Math.round(Number(clock) - Number(changed)));
  if (seconds < 1) return 'Track change just now';
  if (seconds < 60) return `Track change ${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `Track change ${minutes}m${remainder ? ` ${remainder}s` : ''} ago`;
}

export function dbText(value: number | undefined): string {
  return Number.isFinite(value) ? `${(value as number).toFixed(1)} dBFS` : '—';
}

export function dbMeterPercent(value: number | undefined): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, (((value as number) + 90) / 90) * 100));
}

export function validLevelHistory(value: unknown): Array<[number, number]> {
  if (!Array.isArray(value) || value.length !== 40) return [];
  const history: Array<[number, number]> = [];
  for (const pair of value) {
    if (
      !Array.isArray(pair)
      || pair.length !== 2
      || !Number.isFinite(pair[0])
      || !Number.isFinite(pair[1])
      || Number(pair[0]) < -90
      || Number(pair[0]) > 0
      || Number(pair[1]) < -90
      || Number(pair[1]) > 0
    ) return [];
    history.push([Number(pair[0]), Number(pair[1])]);
  }
  return history;
}

export function validLevelHistorySamples(value: unknown, history: Array<[number, number]>): number {
  if (history.length !== 40 || !Number.isInteger(value) || Number(value) < 0 || Number(value) > 40) return 0;
  return Number(value);
}

/** Map a fixed dBFS history onto an SVG path without inventing samples for an older controller. */
export function levelHistoryPath(
  history: Array<[number, number]>,
  channel: 0 | 1,
  width = 320,
  height = 84,
  samples = history.length,
): string {
  if (history.length !== 40 || samples < 1 || samples > 40) return '';
  const first = 40 - Math.floor(samples);
  return history.slice(first).map((pair, offset) => {
    const index = first + offset;
    const x = (index / 39) * width;
    const db = Math.max(-90, Math.min(0, pair[channel]));
    const y = height - ((db + 90) / 90) * height;
    return `${offset === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
}

export function levelHistoryPrerollWidth(samples: number, width = 320): number {
  if (!Number.isInteger(samples) || samples <= 0) return width;
  if (samples >= 40) return 0;
  return ((40 - samples) / 39) * width;
}

export function audioInputClipping(telemetry: AudioTelemetryFeed | undefined): boolean {
  return telemetry?.clip === true
    || (Number.isFinite(telemetry?.meter) && Number(telemetry?.meter) >= 0.999);
}

/** Render-rate truth comes from the controller's one-second guard, not a console-side timer. */
export function audioRenderRateWarning(
  telemetry: AudioTelemetryFeed | undefined,
  explicitWarning = '',
): string {
  const explicit = explicitWarning.trim();
  if (explicit) return explicit;
  if (telemetry?.renderRateShortfall !== true) return '';
  const actual = positive(telemetry.renderFps);
  const target = positive(telemetry.renderTargetFps);
  if (actual != null && target != null) {
    return `Audio rendering cannot keep up: ${actual.toFixed(1)} fps of ${target.toFixed(1)} fps target. The room may look choppy.`;
  }
  return 'Audio rendering cannot keep up with its configured rate. The room may look choppy.';
}
